import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Refresh from "@mui/icons-material/Refresh";
import {
	Box,
	IconButton,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import {
	NAVIGATORS,
	type DetailLevel,
	type NavigatorKind,
	type TimelineView,
} from "../models";

export interface NavigatorState {
	total: number;
	current: number | null;
	previous: boolean;
	next: boolean;
}
interface Props {
	start: Moment | null;
	end: Moment | null;
	onStart: (value: Moment | null) => void;
	onEnd: (value: Moment | null) => void;
	apply: () => void;
	busy: boolean;
	invalid: boolean;
	view: TimelineView;
	onView: (view: TimelineView) => void;
	detail: DetailLevel;
	onDetail: (detail: DetailLevel) => void;
	navigators: Record<NavigatorKind, NavigatorState>;
	navigate: (kind: NavigatorKind, direction: -1 | 1) => void;
	timeZone: string;
}
export function TimelineToolbar(props: Props) {
	return (
		<Box className="timeline-toolbar">
			<Box
				sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
			>
				<DateTimePicker
					label="Start"
					value={props.start}
					onChange={props.onStart}
					timezone={props.timeZone}
					format="MM/DD/YYYY HH:mm"
					ampm={false}
					slotProps={{ textField: { size: "small", sx: { width: 207 } } }}
				/>
				<DateTimePicker
					label="End"
					value={props.end}
					onChange={props.onEnd}
					timezone={props.timeZone}
					format="MM/DD/YYYY HH:mm"
					ampm={false}
					slotProps={{ textField: { size: "small", sx: { width: 207 } } }}
				/>
				<Tooltip title="Refresh — apply date range">
					<span>
						<IconButton
							size="small"
							aria-label="Refresh — apply date range"
							onClick={props.apply}
							disabled={props.busy || props.invalid}
							sx={{
								bgcolor: "primary.main",
								color: "white",
								width: 28,
								height: 28,
								"&:hover": { bgcolor: "primary.dark" },
								"&.Mui-disabled": {
									bgcolor: "action.disabledBackground",
									color: "action.disabled",
								},
							}}
						>
							<Refresh sx={{ fontSize: 18 }} />
						</IconButton>
					</span>
				</Tooltip>
				<Box className="timeline-toggle" sx={{ ml: 1.5 }}>
					<Typography variant="caption">View</Typography>
					<ToggleButtonGroup
						size="small"
						exclusive
						value={props.view}
						onChange={(_, value: TimelineView | null) =>
							value && props.onView(value)
						}
						aria-label="Timeline view"
					>
						<ToggleButton value="parallel">Parallel billets</ToggleButton>
						<ToggleButton value="stands">Key stands</ToggleButton>
					</ToggleButtonGroup>
				</Box>
				<Box className="timeline-toggle">
					<Typography variant="caption">Detail</Typography>
					<ToggleButtonGroup
						size="small"
						exclusive
						value={props.detail}
						onChange={(_, value: DetailLevel | null) =>
							value && props.onDetail(value)
						}
						aria-label="Timeline detail"
					>
						<ToggleButton value="individual">Individual billets</ToggleButton>
						<ToggleButton value="summary">Summary</ToggleButton>
					</ToggleButtonGroup>
				</Box>
			</Box>
			<Box
				sx={{
					display: "flex",
					gap: 0.75,
					alignItems: "center",
					flexWrap: "wrap",
					mt: 1,
				}}
			>
				{NAVIGATORS.map(({ kind, label, color }) => {
					const state = props.navigators[kind];
					return (
						<Box
							key={kind}
							className="timeline-navigator"
							sx={{
								color,
								borderColor: `${color}66`,
								backgroundColor: `${color}08`,
							}}
						>
							<IconButton
								size="small"
								aria-label={`Previous ${label}`}
								disabled={!state.previous || props.busy}
								onClick={() => props.navigate(kind, -1)}
								color="inherit"
							>
								<ChevronLeft fontSize="small" />
							</IconButton>
							<Box sx={{ textAlign: "center", minWidth: 64 }}>
								<Typography component="div" variant="caption">
									{label}
								</Typography>
								<Typography
									component="div"
									variant="caption"
									aria-live="polite"
								>
									{state.current === null
										? state.total
										: `${state.current + 1} / ${state.total}`}
								</Typography>
							</Box>
							<IconButton
								size="small"
								aria-label={`Next ${label}`}
								disabled={!state.next || props.busy}
								onClick={() => props.navigate(kind, 1)}
								color="inherit"
							>
								<ChevronRight fontSize="small" />
							</IconButton>
						</Box>
					);
				})}
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ ml: "auto" }}
				>
					Local · {props.timeZone}
				</Typography>
			</Box>
		</Box>
	);
}
